"use client";

import Image from "next/image";
import { useTranslations } from "next-intl";

import { Link } from "@/navigation";

const IMAGE_BASE = "/image/new-version/casting/Die-Casting/Applications";

const CARDS = [
	{
		keyPrefix: "applicationsCard1",
		image: `${IMAGE_BASE}/aerospace&defense.webp`,
		href: "/industry/aerospace",
	},
	{
		keyPrefix: "applicationsCard2",
		image: `${IMAGE_BASE}/automotive&electrification.webp`,
		href: "/industry/automotive",
	},
	{
		keyPrefix: "applicationsCard3",
		image: `${IMAGE_BASE}/electronics&telecommunications.webp`,
		href: "/industry/consumer-electronics",
	},
	{
		keyPrefix: "applicationsCard4",
		image: `${IMAGE_BASE}/energy&Power-systems.webp`,
		href: "/industry/renewable-energy",
	},
	{
		keyPrefix: "applicationsCard5",
		image: `${IMAGE_BASE}/industrial-equipment.webp`,
		href: "/industry/industry-equipment",
	},
	{
		keyPrefix: "applicationsCard6",
		image: `${IMAGE_BASE}/medical-device-manufacturing.webp`,
		href: "/industry/medical-device",
	},
] as const;

const ITEMS = [
	"applicationsItem1",
	"applicationsItem2",
	"applicationsItem3",
	"applicationsItem4",
	"applicationsItem5",
	"applicationsItem6",
] as const;

export default function DieCastingApplications() {
	const t = useTranslations("new-die-casting");

	return (
		<section className="bg-[#f5fbe9] py-8 sm:py-10 md:py-12 lg:py-16">
			<div className="container">
				<div className="grid grid-cols-1 gap-6 lg:grid-cols-[minmax(0,1fr)_minmax(0,1.6fr)] lg:gap-10">
					<div className="flex flex-col">
						<div className="mb-4">
							<h2 className="text-2xl font-bold leading-tight text-black sm:text-[30px] md:text-4xl">
								<span className="block">{t("applicationsTitleLine1")}</span>
								<span className="flex min-w-0 items-center gap-3">
									<span className="min-w-0 break-words">{t("applicationsTitleLine2")}</span>
									<span
										className="mb-[0.18em] h-[3px] w-12 shrink-0 self-end bg-themePrimary400"
										aria-hidden
									/>
								</span>
							</h2>
						</div>
						<p className="text-sm leading-relaxed text-black md:text-base">{t("applicationsDesc")}</p>
					</div>

					<div className="grid grid-cols-2 gap-3 sm:grid-cols-3 sm:gap-4">
						{CARDS.map((card, index) => {
							const isDefaultActive = index === CARDS.length - 1;

							return (
								<Link
									key={card.keyPrefix}
									href={card.href}
									className={`group relative aspect-[4/2.4] w-full overflow-hidden rounded-2xl border transition-all hover:border-themePrimary400 hover:bg-themePrimary400 hover:shadow-md focus-visible:border-themePrimary400 focus-visible:bg-themePrimary400 focus-visible:shadow-md focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-themePrimary400 focus-visible:ring-offset-2 ${
										isDefaultActive
											? "border-themePrimary400 bg-themePrimary400 shadow-md"
											: "border-transparent bg-[#eef0f2]"
									}`}
								>
									<div className="absolute left-3 top-3 h-12 w-12 overflow-hidden rounded-full sm:h-14 sm:w-14 md:h-16 md:w-16">
										<Image
											src={card.image}
											alt={t(`${card.keyPrefix}Alt` as any)}
											fill
											className="object-cover"
											sizes="64px"
										/>
									</div>
									<span
										className={`absolute bottom-3 right-3 text-right text-xs font-semibold leading-tight transition-colors group-hover:text-white group-focus-visible:text-white sm:text-sm md:text-base ${
											isDefaultActive ? "text-white" : "text-black"
										}`}
									>
										{t(`${card.keyPrefix}Label` as any)}
									</span>
								</Link>
							);
						})}
					</div>
				</div>

				<div className="mt-8 grid grid-cols-1 gap-x-8 gap-y-5 md:mt-10 md:grid-cols-2 lg:mt-12">
					{ITEMS.map((prefix) => (
						<div key={prefix} className="flex items-start gap-3">
							<svg
								className="mt-1 h-5 w-5 shrink-0 text-themePrimary400"
								viewBox="0 0 20 20"
								fill="currentColor"
								aria-hidden
							>
								<path
									fillRule="evenodd"
									d="M10 18a8 8 0 100-16 8 8 0 000 16zm3.707-9.293a1 1 0 00-1.414-1.414L9 10.586 7.707 9.293a1 1 0 00-1.414 1.414l2 2a1 1 0 001.414 0l4-4z"
									clipRule="evenodd"
								/>
							</svg>
							<p className="text-sm leading-relaxed text-black md:text-base">
								<span className="font-bold">{t(`${prefix}Title` as any)}</span> {t(`${prefix}Desc` as any)}
							</p>
						</div>
					))}
				</div>
			</div>
		</section>
	);
}
